import { FormEvent, useEffect, useMemo, useState } from "react";
import {
  CasaNativaClient,
  CasaNativaClientError,
  type CasaNativaMenuItem,
  type CasaNativaPublicOrderResult,
  type CasaNativaPublicSnapshot,
} from "./CasaNativaClient";
import "./casa-nativa-menu-depth.css";

const money = new Intl.NumberFormat("es-AR", {
  style: "currency",
  currency: "ARS",
  maximumFractionDigits: 0,
});

function formatMoney(minor: number): string {
  return money.format(minor / 100);
}

function shortOrderId(id: string): string {
  return id.replace("cd_ord_casa_", "#").toUpperCase();
}

function formatPublishedAt(value: string | null): string {
  if (!value) return "";
  return new Intl.DateTimeFormat("es-AR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "America/Argentina/Buenos_Aires",
  }).format(new Date(value));
}

function MenuItemRow({
  item,
  quantity,
  selectedVariantId,
  onVariantChange,
  onChange,
}: {
  item: CasaNativaMenuItem;
  quantity: number;
  selectedVariantId: string | null;
  onVariantChange: (variantId: string) => void;
  onChange: (quantity: number) => void;
}) {
  const tags = [...item.dietaryClaims, ...item.allergens.map((allergen) => `Contiene ${allergen}`)];
  const selectedVariant = item.variants.find((variant) => variant.id === selectedVariantId)
    ?? item.variants.find((variant) => variant.isDefault)
    ?? item.variants[0]
    ?? null;
  const displayPrice = selectedVariant?.priceMinor ?? item.unitPriceMinor;
  return <article className={`casa-public-item casa-menu-depth-item ${item.available ? "" : "is-unavailable"}`}>
    {item.imageUrl ? <img className="casa-menu-depth-image" src={item.imageUrl} alt="" loading="lazy" /> : <div className="casa-menu-depth-image-placeholder" aria-hidden="true"><span>CN</span></div>}
    <div className="casa-public-item-copy casa-menu-depth-copy">
      <div className="casa-public-item-heading">
        <h3>{item.name}</h3>
        <strong>{formatMoney(displayPrice)}</strong>
      </div>
      <p>{item.description}</p>
      {item.variants.length > 1 && <label className="casa-variant-picker">
        <span>Opción</span>
        <select aria-label={`Opción de ${item.name}`} value={selectedVariant?.id ?? ""} onChange={(event) => onVariantChange(event.target.value)}>
          {item.variants.map((variant) => <option key={variant.id} value={variant.id}>{variant.name} · {formatMoney(variant.priceMinor)}</option>)}
        </select>
      </label>}
      {tags.length > 0 && <div className="casa-menu-tags" aria-label={`Información de ${item.name}`}>
        {tags.map((tag) => <span key={tag}>{tag}</span>)}
      </div>}
      {!item.available && <span className="casa-sold-out">{item.availabilityReason || "No disponible"}</span>}
    </div>
    {item.available && <div className="casa-stepper" aria-label={`Cantidad de ${item.name}`}>
      <button type="button" aria-label={`Quitar ${item.name}`} disabled={quantity === 0} onClick={() => onChange(Math.max(0, quantity - 1))}>−</button>
      <output aria-live="polite">{quantity}</output>
      <button type="button" aria-label={`Agregar ${item.name}`} disabled={quantity >= 8} onClick={() => onChange(Math.min(8, quantity + 1))}>+</button>
    </div>}
  </article>;
}

export function CasaNativaPublic({ client }: { client?: CasaNativaClient }) {
  const api = useMemo(() => client ?? new CasaNativaClient(), [client]);
  const [snapshot, setSnapshot] = useState<CasaNativaPublicSnapshot | null>(null);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [selectedVariants, setSelectedVariants] = useState<Record<string, string>>({});
  const [lineNotes, setLineNotes] = useState<Record<string, string>>({});
  const [fulfillmentType, setFulfillmentType] = useState<"pickup" | "delivery" | "dine_in">("pickup");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<CasaNativaPublicOrderResult["order"] | null>(null);

  useEffect(() => {
    let active = true;
    let timer: number | null = null;
    const refresh = async () => {
      try {
        const live = await api.publicState();
        if (!active) return;
        setSnapshot(live);
        setQuantities((current) => {
          const next = { ...current };
          for (const item of live.menu) if (!item.available) next[item.id] = 0;
          return next;
        });
        setError(null);
      } catch {
        if (active && !snapshot) setError("No pudimos cargar el menú en este momento.");
      } finally {
        if (active) timer = window.setTimeout(refresh, 4_000);
      }
    };
    void refresh();
    const focus = () => void refresh();
    window.addEventListener("focus", focus);
    return () => {
      active = false;
      if (timer !== null) window.clearTimeout(timer);
      window.removeEventListener("focus", focus);
    };
  // snapshot is intentionally excluded: the loop owns refresh cadence.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  const selected = useMemo(() => {
    if (!snapshot) return [];
    return snapshot.menu
      .map((item) => ({
        item,
        variant: item.variants.find((candidate) => candidate.id === selectedVariants[item.id])
          ?? item.variants.find((candidate) => candidate.isDefault)
          ?? item.variants[0]
          ?? null,
        quantity: quantities[item.id] ?? 0,
      }))
      .filter(({ item, quantity }) => item.available && quantity > 0);
  }, [snapshot, quantities, selectedVariants]);
  const totalMinor = selected.reduce((sum, { item, variant, quantity }) => sum + (variant?.priceMinor ?? item.unitPriceMinor) * quantity, 0);
  const totalUnits = selected.reduce((sum, { quantity }) => sum + quantity, 0);
  const categories = snapshot
    ? [...new Set(snapshot.menu.map((item) => item.category))]
    : [];

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (selected.length === 0) {
      setError("Elegí al menos un producto para continuar.");
      return;
    }
    const data = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      const requestedAt = String(data.get("requestedAt") ?? "").trim();
      const orderNotes = String(data.get("notes") ?? "").trim();
      const result = await api.createPublicOrder({
        contactName: String(data.get("contactName") ?? ""),
        phone: String(data.get("phone") ?? ""),
        fulfillmentType,
        ...(fulfillmentType === "delivery"
          ? { address: String(data.get("address") ?? "") }
          : {}),
        ...(requestedAt ? { requestedAt: new Date(requestedAt).toISOString() } : {}),
        ...(orderNotes ? { notes: orderNotes } : {}),
        items: selected.map(({ item, variant, quantity }) => ({
          itemId: item.id,
          ...(variant ? { variantId: variant.id } : {}),
          quantity,
          ...(lineNotes[item.id]?.trim() ? { notes: lineNotes[item.id].trim() } : {}),
        })),
      });
      setSnapshot({ restaurant: result.restaurant, menu: result.menu, menuPublication: result.menuPublication });
      setQuantities({});
      setLineNotes({});
      setConfirmation(result.order);
      event.currentTarget.reset();
      setFulfillmentType("pickup");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (cause) {
      if (cause instanceof CasaNativaClientError && cause.code === "status_transition_invalid") {
        setError("Uno de los productos cambió de disponibilidad. Actualizamos el menú para que puedas elegir nuevamente.");
        try { setSnapshot(await api.publicState()); } catch { /* keep original error */ }
      } else {
        setError(cause instanceof Error ? cause.message : "No pudimos enviar el pedido.");
      }
    } finally {
      setBusy(false);
    }
  }

  if (!snapshot) {
    return <main className="casa-public casa-public-loading" id="main-content" aria-live="polite">
      <div className="casa-public-loader"><span>CN</span><p>{error ?? "Abriendo Casa Nativa…"}</p></div>
    </main>;
  }

  return <main className="casa-public" id="main-content">
    <header className="casa-public-hero">
      <nav className="casa-public-topbar" aria-label="Casa Nativa">
        <a className="casa-wordmark" href="#inicio" aria-label="Casa Nativa, inicio"><span>CN</span><strong>Casa Nativa</strong></a>
        <a className="casa-topbar-link" href="#menu">Ver carta</a>
      </nav>
      <div className="casa-public-hero-inner" id="inicio">
        <div>
          <span className="casa-demo-label">{snapshot.restaurant.demoLabel}</span>
          <p className="casa-kicker">Restaurante · Córdoba</p>
          <h1>{snapshot.restaurant.name}</h1>
          <p className="casa-tagline">{snapshot.restaurant.tagline}</p>
          <div className="casa-hero-meta">
            <span>{snapshot.restaurant.hours}</span>
            <span>{snapshot.restaurant.address}</span>
          </div>
          <a className="casa-hero-button" href="#menu">Elegir mi pedido</a>
        </div>
        <div className="casa-hero-art" aria-hidden="true">
          <div className="casa-plate"><span>hecho<br />en casa</span></div>
        </div>
      </div>
    </header>

    {confirmation && <section className="casa-order-confirmation" role="status" aria-live="polite">
      <div className="casa-confirmation-mark" aria-hidden="true">✓</div>
      <div>
        <p className="casa-kicker">Pedido recibido</p>
        <h2>{shortOrderId(confirmation.id)} ya está en Casa Nativa</h2>
        <p>{confirmation.fulfillmentType === "delivery" ? "Lo preparamos para enviar." : confirmation.fulfillmentType === "dine_in" ? "Lo preparamos para servir en el salón." : "Lo preparamos para retirar."} Total: <strong>{formatMoney(confirmation.totalMinor)}</strong>.</p>
      </div>
      <button type="button" onClick={() => setConfirmation(null)}>Hacer otro pedido</button>
    </section>}

    <section className="casa-public-menu" id="menu" aria-labelledby="casa-menu-title">
      <div className="casa-section-heading">
        <div><p className="casa-kicker">Nuestra carta</p><h2 id="casa-menu-title">Comé rico. Elegí simple.</h2></div>
        <div className="casa-menu-publication">
          <strong>Carta publicada · v{snapshot.menuPublication.versionNumber ?? "—"}</strong>
          <span>{snapshot.menuPublication.publishedAt ? `Actualizada ${formatPublishedAt(snapshot.menuPublication.publishedAt)}` : "Publicación canónica activa"}</span>
        </div>
      </div>
      <p className="casa-menu-depth-note">La carta muestra la publicación activa: opciones y precios por variante, imágenes, información alimentaria y disponibilidad provienen del producto Menu.</p>

      <div className="casa-order-layout">
        <div className="casa-menu-list">
          {categories.map((category) => <section key={category} aria-labelledby={`casa-category-${category}`}>
            <h2 className="casa-category-title" id={`casa-category-${category}`}>{category}</h2>
            {snapshot.menu.filter((item) => item.category === category).map((item) => <MenuItemRow
              key={item.id}
              item={item}
              quantity={quantities[item.id] ?? 0}
              selectedVariantId={selectedVariants[item.id] ?? null}
              onVariantChange={(variantId) => setSelectedVariants((current) => ({ ...current, [item.id]: variantId }))}
              onChange={(quantity) => setQuantities((current) => ({ ...current, [item.id]: quantity }))}
            />)}
          </section>)}
        </div>

        <aside className="casa-cart" aria-labelledby="casa-cart-title">
          <div className="casa-cart-heading">
            <div><p className="casa-kicker">Tu pedido</p><h2 id="casa-cart-title">{totalUnits === 0 ? "Elegí algo rico" : `${totalUnits} ${totalUnits === 1 ? "producto" : "productos"}`}</h2></div>
            <strong>{formatMoney(totalMinor)}</strong>
          </div>
          {selected.length === 0
            ? <p className="casa-cart-empty">Usá los botones + de la carta para agregar productos.</p>
            : <ul className="casa-cart-lines">{selected.map(({ item, variant, quantity }) => <li key={item.id}>
  <span>{quantity} × {item.name}{variant ? ` · ${variant.name}` : ""}</span>
  <strong>{formatMoney((variant?.priceMinor ?? item.unitPriceMinor) * quantity)}</strong>
  <label className="casa-line-note">Nota para este producto<textarea maxLength={240} value={lineNotes[item.id] ?? ""} onChange={(event) => setLineNotes((current) => ({ ...current, [item.id]: event.target.value }))} placeholder="Ej. sin cebolla" /></label>
</li>)}</ul>}

          <form className="casa-checkout" onSubmit={submit}>
            <fieldset>
              <legend>¿Cómo lo querés?</legend>
              <div className="casa-choice-row">
                <label className={fulfillmentType === "pickup" ? "is-selected" : ""}><input type="radio" name="fulfillment" value="pickup" checked={fulfillmentType === "pickup"} onChange={() => setFulfillmentType("pickup")} />Retiro</label>
                <label className={fulfillmentType === "delivery" ? "is-selected" : ""}><input type="radio" name="fulfillment" value="delivery" checked={fulfillmentType === "delivery"} onChange={() => setFulfillmentType("delivery")} />Delivery</label>
<label className={fulfillmentType === "dine_in" ? "is-selected" : ""}><input type="radio" name="fulfillment" value="dine_in" checked={fulfillmentType === "dine_in"} onChange={() => setFulfillmentType("dine_in")} />En el salón</label>
              </div>
            </fieldset>
            <label>Nombre<input name="contactName" autoComplete="name" maxLength={80} required placeholder="Juan" /></label>
            <label>Teléfono <span>opcional</span><input name="phone" inputMode="tel" autoComplete="tel" maxLength={40} placeholder="351 555 0100" /></label>
            {fulfillmentType === "delivery" && <label>Dirección<input name="address" autoComplete="street-address" maxLength={160} required placeholder="San Martín 250" /></label>}
            <label>¿Para cuándo? <span>opcional</span><input name="requestedAt" type="datetime-local" /></label>
            <label>Nota del pedido <span>opcional</span><textarea name="notes" maxLength={500} placeholder="Ej. tocar timbre al llegar" /></label>
            {fulfillmentType === "dine_in" && <p className="casa-checkout-hint">El pedido queda marcado como consumo en salón. La asignación de mesa pertenece al módulo Seating y no se simula en A2.</p>}
            {error && <p className="casa-form-error" role="alert">{error}</p>}
            <button className="casa-submit-order" type="submit" disabled={busy || selected.length === 0}>{busy ? "Enviando…" : `Confirmar · ${formatMoney(totalMinor)}`}</button>
          </form>
        </aside>
      </div>
    </section>

    <footer className="casa-public-footer">
      <div><span className="casa-footer-mark">CN</span><strong>Casa Nativa</strong><p>{snapshot.restaurant.tagline}</p></div>
      <div><strong>Visitanos</strong><p>{snapshot.restaurant.address}</p><p>{snapshot.restaurant.hours}</p></div>
      <div><strong>Contacto</strong><p>{snapshot.restaurant.phone}</p></div>
    </footer>
  </main>;
}
